import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Button,
    Chip,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress,
    LinearProgress,
    Divider,
    ButtonGroup,
    TextField,
    Stack,
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Refresh as RefreshIcon,
    Settings as SettingsIcon,
    RestartAlt as RestartIcon,
    Scale as ScaleIcon,
    Thermostat as TempIcon,
    WaterDrop as HumidityIcon,
    Battery3Bar as BatteryIcon,
    SignalWifi4Bar as SignalIcon,
    Update as UpdateIcon,
    ContentCopy as CopyIcon,
    Fingerprint as FingerprintIcon,
    Router as RouterIcon,
    CalendarMonth as CalendarIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
} from 'chart.js';
import { format, parseISO, isToday, subDays, subMonths, startOfDay, endOfDay } from 'date-fns';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { fetchApiariesAndGroups } from '@store/slices/locationsSlice';
import { updateDevice } from '@store/slices/devicesSlice';
import CalibrationDialog from '@components/devices/CalibrationDialog';
import DeviceDialog from '@components/devices/DeviceDialog';
import { Device } from '@types';
import { devicesApi } from '@api/devices';
import api from '@api/index';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    ChartTooltip,
    Legend
);

interface DeviceStatus {
    online: boolean;
    battery_voltage: number;
    signal_strength: number;
    last_seen: string;
    last_message_received?: string;
}

interface SensorData {
    weight_kg: number;
    temperatures_c: number[];
    humidity_pct: number;
    timestamp: string;
}

const DeviceDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const [device, setDevice] = useState<Device | null>(null);
    const [status, setStatus] = useState<DeviceStatus | null>(null);
    const [sensorData, setSensorData] = useState<SensorData | null>(null);
    const [historicalData, setHistoricalData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [showCalibration, setShowCalibration] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [copiedKey, setCopiedKey] = useState(false);

    // ── Date range filter ─────────────────────────────────────────────────
    type Preset = '24h' | '7d' | '30d' | 'custom';
    const [preset, setPreset] = useState<Preset>('30d');
    const [customStart, setCustomStart] = useState<string>(
        format(subMonths(new Date(), 2), 'yyyy-MM-dd')  // default: covers seeded data
    );
    const [customEnd, setCustomEnd] = useState<string>(
        format(new Date(), 'yyyy-MM-dd')
    );
    const [historyLoading, setHistoryLoading] = useState(false);
    // ─────────────────────────────────────────────────────────────────────

    const { apiaries, loadingState: locationsLoading } = useAppSelector((state) => state.locations);

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedKey(true);
            setTimeout(() => setCopiedKey(false), 2000);
        });
    };

    useEffect(() => {
        if (locationsLoading === 'idle') {
            dispatch(fetchApiariesAndGroups());
        }
        loadDeviceData();

        // Auto-refresh every 30 seconds
        const interval = setInterval(refreshStatus, 30000);
        return () => clearInterval(interval);
    }, [id]);

    // Re-fetch history whenever preset or custom dates change
    useEffect(() => {
        if (id) fetchHistory();
    }, [id, preset, customStart, customEnd]);

    /** Compute start/end ISO strings from current filter state */
    const getDateRange = useCallback(() => {
        const now = new Date();
        switch (preset) {
            case '24h': return {
                start: subDays(now, 1).toISOString(),
                end:   now.toISOString(),
                limit: 200,
            };
            case '7d': return {
                start: subDays(now, 7).toISOString(),
                end:   now.toISOString(),
                limit: 500,
            };
            case '30d': return {
                start: subDays(now, 30).toISOString(),
                end:   now.toISOString(),
                limit: 1500,
            };
            case 'custom': return {
                start: startOfDay(new Date(customStart)).toISOString(),
                end:   endOfDay(new Date(customEnd)).toISOString(),
                limit: 2000,
            };
        }
    }, [preset, customStart, customEnd]);

    const fetchHistory = useCallback(async () => {
        if (!id) return;
        setHistoryLoading(true);
        try {
            const { start, end, limit } = getDateRange();
            const res = await api.get(
                `/devices/${id}/history/?limit=${limit}&start_date=${encodeURIComponent(start)}&end_date=${encodeURIComponent(end)}`
            );
            setHistoricalData(res.data.data || res.data);
        } catch (err) {
            console.error('Error fetching history:', err);
        } finally {
            setHistoryLoading(false);
        }
    }, [id, getDateRange]);

    const loadDeviceData = async () => {
        try {
            const [deviceRes, statusRes, dataRes] = await Promise.all([
                api.get(`/devices/${id}/`),
                api.get(`/devices/${id}/status/`),
                api.get(`/devices/${id}/latest-data/`),
            ]);

            setDevice(deviceRes.data.data || deviceRes.data);
            setStatus(statusRes.data.data || statusRes.data);
            setSensorData(dataRes.data.data || dataRes.data);
        } catch (error) {
            console.error('Error loading device data:', error);
        } finally {
            setLoading(false);
        }
    };

    const refreshStatus = async () => {
        setRefreshing(true);
        try {
            const deviceId = parseInt(id || '0');
            const [statusRes, dataRes] = await Promise.all([
                devicesApi.getDeviceStatus(deviceId),
                devicesApi.getDeviceLatestData(deviceId),
            ]);

            setStatus(statusRes.data.data || statusRes.data);
            setSensorData(dataRes.data.data || dataRes.data);
        } catch (error) {
            console.error('Error refreshing status:', error);
        } finally {
            setRefreshing(false);
        }
    };

    const handleRestart = async () => {
        if (!confirm('Are you sure you want to restart this device?')) return;

        try {
            await devicesApi.restartDevice(parseInt(id || '0'));
            alert('Restart command sent. Device will reboot shortly.');
        } catch (error) {
            alert('Failed to send restart command.');
        }
    };

    const handleSaveDevice = async (deviceData: Partial<Device>) => {
        if (!device) return;
        try {
            const result = await dispatch(updateDevice({ id: device.id, data: deviceData }));
            if (updateDevice.fulfilled.match(result)) {
                setDevice({ ...device, ...deviceData } as Device);
                setShowEditDialog(false);
                loadDeviceData(); // Refresh data to show updated link
            }
        } catch (error) {
            console.error('Error updating device:', error);
        }
    };

    const handleForceRead = async () => {
        try {
            await devicesApi.triggerMeasurement(parseInt(id || '0'));
            alert('Measurement triggered. Data will arrive shortly.');
            setTimeout(refreshStatus, 5000);
        } catch (error) {
            alert('Failed to trigger measurement.');
        }
    };

    // Prepare chart data
    const chartData = {
        labels: historicalData.map(d => {
            const date = parseISO(d.timestamp);
            return isToday(date) ? format(date, 'HH:mm') : format(date, 'MMM d, HH:mm');
        }),
        datasets: [
            {
                label: 'Weight (kg)',
                data: historicalData.map(d => d.weight_kg),
                borderColor: '#F8B133',
                backgroundColor: 'rgba(248, 177, 51, 0.1)',
                tension: 0.4,
                yAxisID: 'y',
            },
            {
                label: 'Temperature (°C)',
                data: historicalData.map(d => d.temperatures_c[0] || null),
                borderColor: '#FF5722',
                backgroundColor: 'rgba(255, 87, 34, 0.1)',
                tension: 0.4,
                yAxisID: 'y1',
            },
            {
                label: 'Humidity (%)',
                data: historicalData.map(d => d.humidity_pct),
                borderColor: '#2196F3',
                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                tension: 0.4,
                yAxisID: 'y1', // Share Y1 with temperature
            }
        ],
    };

    const presetLabel: Record<string, string> = {
        '24h': 'Last 24 h',
        '7d':  'Last 7 days',
        '30d': 'Last 30 days',
        'custom': `${customStart} → ${customEnd}`,
    };

    const chartOptions = {
        responsive: true,
        interaction: {
            mode: 'index' as const,
            intersect: false,
        },
        plugins: {
            legend: {
                display: true,
                position: 'top' as const,
            },
            title: {
                display: true,
                text: `Environmental & Weight History  ·  ${presetLabel[preset]}  (${historicalData.length} points)`,
            },
        },
        scales: {
            y: {
                type: 'linear' as const,
                display: true,
                position: 'left' as const,
                title: { display: true, text: 'Weight (kg)' }
            },
            y1: {
                type: 'linear' as const,
                display: true,
                position: 'right' as const,
                grid: { drawOnChartArea: false },
                title: { display: true, text: 'Temp (°C) / Humidity (%)' }
            },
        },
    };

    if (loading) {
        return (
            <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (!device) {
        return (
            <Container>
                <Alert severity="error">Device not found</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton onClick={() => navigate('/devices')} sx={{ mr: 2 }}>
                    <BackIcon />
                </IconButton>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h4">{device.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {device.key} • {device.firmware_version || 'Unknown version'}
                    </Typography>
                </Box>

                <Chip
                    label={status?.online ? 'Online' : 'Offline'}
                    color={status?.online ? 'success' : 'error'}
                    sx={{ mr: 1 }}
                />

                <Tooltip title="Refresh">
                    <IconButton onClick={refreshStatus} disabled={refreshing}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Settings / Edit Device">
                    <IconButton onClick={() => setShowEditDialog(true)}>
                        <SettingsIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {refreshing && <LinearProgress sx={{ mb: 2 }} />}

            {/* Status Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Battery */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <BatteryIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                    Battery
                                </Typography>
                            </Box>
                            <Typography variant="h4">
                                {status?.battery_voltage?.toFixed(2) ?? '—'}V
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={((status?.battery_voltage || 0) - 2.0) / 1.8 * 100}
                                sx={{ mt: 1 }}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                {/* Signal Strength */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <SignalIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                    Signal
                                </Typography>
                            </Box>
                            <Typography variant="h4">
                                {status?.signal_strength}dBm
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {status && status.signal_strength > -50 ? 'Excellent' :
                                    status && status.signal_strength > -70 ? 'Good' : 'Weak'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Weight */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ScaleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                    Weight
                                </Typography>
                            </Box>
                            <Typography variant="h4">
                                {sensorData?.weight_kg?.toFixed(2) ?? '—'} kg
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Last: {sensorData && format(parseISO(sensorData.timestamp), 'HH:mm')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Temperature */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <TempIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                    Temperature
                                </Typography>
                            </Box>
                            <Typography variant="h4">
                                {sensorData?.temperatures_c[0]?.toFixed(1)}°C
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Avg of {sensorData?.temperatures_c.length} sensors
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Actions */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Device Actions
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            startIcon={<ScaleIcon />}
                            onClick={() => setShowCalibration(true)}
                        >
                            Calibrate Scale
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={handleForceRead}
                        >
                            Trigger Reading
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<RestartIcon />}
                            onClick={handleRestart}
                            color="warning"
                        >
                            Restart Device
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<UpdateIcon />}
                            disabled
                        >
                            Update Firmware
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Chart + Date Filter */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    {/* ── Date range controls ── */}
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        sx={{ mb: 2 }}
                    >
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            Time range:
                        </Typography>

                        <ButtonGroup size="small" variant="outlined">
                            {(['24h', '7d', '30d'] as const).map((p) => (
                                <Button
                                    key={p}
                                    variant={preset === p ? 'contained' : 'outlined'}
                                    onClick={() => setPreset(p)}
                                >
                                    {p === '24h' ? '24 h' : p === '7d' ? '7 days' : '30 days'}
                                </Button>
                            ))}
                            <Button
                                variant={preset === 'custom' ? 'contained' : 'outlined'}
                                onClick={() => setPreset('custom')}
                            >
                                Custom
                            </Button>
                        </ButtonGroup>

                        {preset === 'custom' && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <TextField
                                    label="From"
                                    type="date"
                                    size="small"
                                    value={customStart}
                                    onChange={(e) => setCustomStart(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ max: customEnd }}
                                    sx={{ width: 160 }}
                                />
                                <Typography variant="body2">–</Typography>
                                <TextField
                                    label="To"
                                    type="date"
                                    size="small"
                                    value={customEnd}
                                    onChange={(e) => setCustomEnd(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: customStart, max: format(new Date(), 'yyyy-MM-dd') }}
                                    sx={{ width: 160 }}
                                />
                            </Stack>
                        )}

                        {historyLoading && (
                            <CircularProgress size={18} thickness={5} sx={{ ml: 1 }} />
                        )}

                        <Box sx={{ flexGrow: 1 }} />

                        <Typography variant="caption" color="text.secondary">
                            {historicalData.length} data points
                        </Typography>
                    </Stack>

                    {/* ── Chart ── */}
                    {historicalData.length === 0 && !historyLoading ? (
                        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                            <Typography variant="body1">No data for this time range.</Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                Try selecting a wider range or check that the device has data.
                            </Typography>
                        </Box>
                    ) : (
                        <Line data={chartData} options={chartOptions} />
                    )}
                </CardContent>
            </Card>

            {/* Temperature Details */}
            {sensorData && sensorData.temperatures_c.length > 0 && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Temperature Sensors
                        </Typography>
                        <Grid container spacing={2}>
                            {sensorData.temperatures_c.map((temp, index) => (
                                <Grid item xs={6} sm={3} key={index}>
                                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Sensor {index + 1}
                                        </Typography>
                                        <Typography variant="h5">
                                            {temp.toFixed(1)}°C
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* Device Info */}
            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <FingerprintIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6">Device Information</Typography>
                    </Box>

                    {/* ── Identity ── */}
                    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Identity
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Device Name</Typography>
                            <Typography variant="body1" fontWeight="medium">{device.name}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Device Type</Typography>
                            <Typography variant="body1">{(device as any).type || (device as any).category_name || '—'}</Typography>
                        </Grid>

                        {/* Device Key — most important, shown prominently */}
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Device Key / Serial
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.95rem',
                                        fontWeight: 'bold',
                                        bgcolor: 'action.hover',
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        flexGrow: 1,
                                        wordBreak: 'break-all',
                                        color: device.key ? 'text.primary' : 'text.disabled',
                                    }}
                                >
                                    {device.key || 'Not available'}
                                </Box>
                                {device.key && (
                                    <Tooltip title={copiedKey ? 'Copied!' : 'Copy to clipboard'}>
                                        <IconButton
                                            size="small"
                                            onClick={() => copyToClipboard(device.key)}
                                            color={copiedKey ? 'success' : 'default'}
                                        >
                                            {copiedKey ? <CheckCircleIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Hardware ID</Typography>
                            <Typography
                                variant="body1"
                                sx={{ fontFamily: 'monospace', color: device.hardware_id ? 'text.primary' : 'warning.main' }}
                            >
                                {device.hardware_id || 'Not detected'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Firmware Version</Typography>
                            <Typography
                                variant="body1"
                                sx={{ color: device.firmware_version ? 'text.primary' : 'warning.main' }}
                            >
                                {device.firmware_version || 'Unknown'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Software Version</Typography>
                            <Typography variant="body1">
                                {(device as any).software_version || '—'}
                            </Typography>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    {/* ── Hive / Location ── */}
                    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Hive &amp; Location
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Linked Hive</Typography>
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: 'bold', color: device.hive_name ? 'primary.main' : 'text.disabled' }}
                            >
                                {device.hive_name || 'Unlinked'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Apiary / Location</Typography>
                            <Typography variant="body1">{device.location_name || 'N/A'}</Typography>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    {/* ── Connectivity & Status ── */}
                    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Connectivity &amp; Status
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Last Seen</Typography>
                            <Typography variant="body1" sx={{ color: 'info.main' }}>
                                {status?.last_seen ? format(parseISO(status.last_seen), 'PPpp') : 'Never'}
                            </Typography>
                        </Grid>
                        {status?.last_message_received && (
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">Last Data Received</Typography>
                                <Typography variant="body1" sx={{ color: 'info.main' }}>
                                    {format(parseISO(status.last_message_received), 'PPpp')}
                                </Typography>
                            </Grid>
                        )}
                        {(device as any).ip_address && (
                            <Grid item xs={12} sm={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <RouterIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary">IP Address</Typography>
                                </Box>
                                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                                    {(device as any).ip_address}
                                </Typography>
                            </Grid>
                        )}
                        {(device as any).signal_strength !== undefined && (device as any).signal_strength !== null && (
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">Signal Strength</Typography>
                                <Typography variant="body1">{(device as any).signal_strength} dBm</Typography>
                            </Grid>
                        )}
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    {/* ── Configuration ── */}
                    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Configuration
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Measurement Interval</Typography>
                            <Typography variant="body1">
                                {(device as any).measurement_interval_minutes
                                    ? `Every ${(device as any).measurement_interval_minutes} min`
                                    : '—'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Transmission Interval</Typography>
                            <Typography variant="body1">
                                {(device as any).transmission_interval_minutes
                                    ? `Every ${(device as any).transmission_interval_minutes} min`
                                    : '—'}
                            </Typography>
                        </Grid>
                        {(device as any).installation_date && (
                            <Grid item xs={12} sm={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary">Installation Date</Typography>
                                </Box>
                                <Typography variant="body1">
                                    {format(parseISO((device as any).installation_date), 'PP')}
                                </Typography>
                            </Grid>
                        )}
                        {(device as any).last_maintenance && (
                            <Grid item xs={12} sm={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary">Last Maintenance</Typography>
                                </Box>
                                <Typography variant="body1">
                                    {format(parseISO((device as any).last_maintenance), 'PP')}
                                </Typography>
                            </Grid>
                        )}
                        {(device as any).last_calibration && (
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">Last Calibration</Typography>
                                <Typography variant="body1">
                                    {format(parseISO((device as any).last_calibration), 'PPpp')}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </CardContent>
            </Card>

            {/* Edit Device Dialog */}
            <DeviceDialog
                open={showEditDialog}
                onClose={() => setShowEditDialog(false)}
                onSave={handleSaveDevice}
                device={device}
                apiaries={apiaries}
            />

            {/* Calibration Dialog */}
            <CalibrationDialog
                open={showCalibration}
                onClose={() => setShowCalibration(false)}
                deviceId={parseInt(id || '0')}
                deviceName={device.name}
            />
        </Container>
    );
};

export default DeviceDetail;
